/**
 * TcfReportTile - Entry tile for the Treating Customers Fairly Outcomes Report.
 *
 * Atomic level: Molecule. Mirrors OutcomesReportTile — the six TCF outcomes,
 * each with its conduct score on the left. Scores come from the tcf-report
 * outcomes endpoint; before they load (or if unavailable) it falls back to the
 * six labelled rows with a neutral marker so the tile never renders empty.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../../ui/card';
import { getConductScoreColor } from './ConductScoreIndicator';
import { useTcfOutcomes } from '../../reports/tcf-report/hooks';
import { TCF_OUTCOME_DISPLAY_ORDER } from '../../reports/tcf-report/constants';
import { cleanOutcomeName } from '../../reports/outcomes-monitoring/utils';

interface TcfReportTileProps {
  collectionId: string;
  className?: string;
}

interface OutcomeRow {
  name: string;
  score: number | null;
}

const FALLBACK_ROWS: OutcomeRow[] = [
  { name: 'Fair Treatment', score: null },
  { name: 'Suitable Products', score: null },
  { name: 'Clear Information', score: null },
  { name: 'Suitable Advice', score: null },
  { name: 'Product Performance', score: null },
  { name: 'No Post-Sale Barriers', score: null },
];

const TcfReportTile: React.FC<TcfReportTileProps> = ({ collectionId, className = '' }) => {
  const { data } = useTcfOutcomes(collectionId, {});

  const rows: OutcomeRow[] = data?.outcomes?.length
    ? [...data.outcomes]
        .sort(
          (a, b) =>
            TCF_OUTCOME_DISPLAY_ORDER.indexOf(a.id) - TCF_OUTCOME_DISPLAY_ORDER.indexOf(b.id),
        )
        .map((o) => ({ name: cleanOutcomeName(o.name), score: o.avgConductScore }))
    : FALLBACK_ROWS;

  return (
    <Link to={`/v4/reports/tcf/${collectionId}`} className="block h-full">
      <Card
        className={`bg-white border-slate-200 cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all h-full ${className}`}
      >
        <CardContent className="p-4 flex flex-col gap-3">
          <h3 className="text-[13px] font-bold">TCF Outcomes Report</h3>

          <div className="flex flex-col gap-2">
            {rows.map((outcome) => (
              <div key={outcome.name} className="flex items-center gap-2">
                {outcome.score !== null ? (
                  <span
                    className="w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-extrabold text-white shrink-0"
                    style={{ backgroundColor: getConductScoreColor(outcome.score) }}
                  >
                    {outcome.score.toFixed(1)}
                  </span>
                ) : (
                  <span className="w-6 h-6 rounded-full bg-muted shrink-0" />
                )}
                <span className="text-[11px] font-semibold text-foreground">{outcome.name}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export { TcfReportTile };
export type { TcfReportTileProps };
